import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, X } from "lucide-react";
import { adminListManagers, adminManagerArtists, adminAssignRelease, adminUnassignRelease, adminGetAssignment } from "@/lib/afmApi";

// Super admin (la disquera): asignar un lanzamiento a cualquier subcuenta y artista de su lista,
// con colaboradores de la misma subcuenta (sale en el proyecto de cada uno).
export default function AssignToManager({ release }) {
  const [assignment, setAssignment] = useState(undefined);
  const [editing, setEditing] = useState(false);
  const [managers, setManagers] = useState([]);
  const [mid, setMid] = useState("");
  const [artists, setArtists] = useState([]);
  const [aid, setAid] = useState("");
  const [collabs, setCollabs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetAssignment(release.id).then(setAssignment).catch(() => setAssignment(null));
    adminListManagers().then((rows) => setManagers(rows.filter((m) => m.active))).catch(() => {});
  }, [release.id]);
  useEffect(() => {
    setAid(""); setArtists([]); setCollabs([]);
    if (mid) adminManagerArtists(mid).then((rows) => setArtists(rows.filter((a) => !a.removed))).catch(() => {});
  }, [mid]);

  if (assignment === undefined || (!managers.length && !assignment)) return null;

  const save = async () => {
    setBusy(true);
    try {
      const r = await adminAssignRelease({ release_id: release.id, manager_id: mid, artist_id: aid, collab_ids: collabs.filter((c) => c !== aid), title: release.title || "", release_date: String(release.release_date || "").slice(0, 10) });
      setAssignment(r); setMid(""); setEditing(false);
      toast.success(`Asignado a ${r.manager_name}: ya le aparece en su panel`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo asignar"); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await adminUnassignRelease(release.id); setAssignment(null); toast.success("Asignación quitada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo quitar"); } finally { setBusy(false); }
  };

  const sel = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="assign-to-manager">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600"><Building2 className="h-3.5 w-3.5" /> Subcuenta</div>
      {assignment && !editing ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span>Asignado a <strong>{assignment.manager_name}</strong> · {(assignment.artist_names || [assignment.artist_name]).join(", ")}{assignment.source === "manager" ? " (lo subió la subcuenta)" : ""}</span>
          <span className="flex gap-3">
            <button onClick={() => setEditing(true)} disabled={busy} className="text-xs font-semibold text-slate-500 hover:text-black">Cambiar</button>
            <button onClick={remove} disabled={busy} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600"><X className="h-3.5 w-3.5" /> Quitar</button>
          </span>
        </div>
      ) : (
        <div className="space-y-2">
          <select value={mid} onChange={(e) => setMid(e.target.value)} className={sel} data-testid="assign-manager">
            <option value="">No asignado (cuenta propia del artista)</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          {mid && (
            <select value={aid} onChange={(e) => setAid(e.target.value)} className={sel} data-testid="assign-artist">
              <option value="">{artists.length ? "Elige el artista de su lista…" : "Esa subcuenta no tiene artistas aún"}</option>
              {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          {mid && aid && artists.length > 1 && (
            <div className="rounded-lg border border-slate-200 bg-white p-2">
              <div className="mb-1 text-[11px] font-semibold text-slate-500">¿Colabora otro artista de esta subcuenta?</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {artists.filter((a) => a.id !== aid).map((a) => (
                  <label key={a.id} className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={collabs.includes(a.id)} onChange={(e) => setCollabs((p) => (e.target.checked ? [...p, a.id] : p.filter((x) => x !== a.id)))} /> {a.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            {mid && aid && <button onClick={save} disabled={busy} className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Asignar a la subcuenta</button>}
            {editing && <button onClick={() => setEditing(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs">Cancelar</button>}
          </div>
        </div>
      )}
    </div>
  );
}
